import { z } from "zod";
import { competenciaAtual, competenciaSchema, dataParaTexto, dataSchema, hojeEmTexto, nomeDoMes, somarMeses, textoParaData } from "./competencia";

/** Período da análise do financeiro: de um dia a outro, inclusive ("AAAA-MM-DD"). */
export type Periodo = { de: string; ate: string };

const DIA_MS = 86_400_000;
export const MESES_NO_MAXIMO = 36;

const ultimoDia = (m: string) => dataParaTexto(new Date(textoParaData(`${somarMeses(m, 1)}-01`).getTime() - DIA_MS));
const diasDoMes = (m: string) => Number(ultimoDia(m).slice(8, 10));
const somarDias = (dia: string, n: number) => dataParaTexto(new Date(textoParaData(dia).getTime() + n * DIA_MS));
const diasEntre = (de: string, ate: string) => Math.round((textoParaData(ate).getTime() - textoParaData(de).getTime()) / DIA_MS) + 1;

export const periodoSchema = z
  .object({ de: dataSchema, ate: dataSchema })
  .refine((p) => p.de <= p.ate, "O início vem antes do fim.")
  .refine((p) => mesesDoPeriodo(p).length <= MESES_NO_MAXIMO, `No máximo ${MESES_NO_MAXIMO} meses.`);

/** O que os endpoints do financeiro aceitam: o mês de sempre ou um período. */
export const mesOuPeriodoSchema = z.union([z.object({ competencia: competenciaSchema }), periodoSchema]);
export type MesOuPeriodo = z.infer<typeof mesOuPeriodoSchema>;
export const paraPeriodo = (e: MesOuPeriodo): Periodo => ("competencia" in e ? periodoDoMes(e.competencia) : { de: e.de, ate: e.ate });

export const periodoDoMes = (m: string): Periodo => ({ de: `${m}-01`, ate: ultimoDia(m) });

/** Os meses que o período toca, em ordem. */
export function mesesDoPeriodo(p: Periodo): string[] {
  const meses: string[] = [];
  for (let m = p.de.slice(0, 7); m <= p.ate.slice(0, 7); m = somarMeses(m, 1)) meses.push(m);
  return meses;
}

/** Começa no dia 1 e termina no último dia de um mês (pode ser mais de um mês). */
export const soMesesInteiros = (p: Periodo) => p.de.endsWith("-01") && p.ate === ultimoDia(p.ate.slice(0, 7));

/** A competência, quando o período é exatamente um mês. */
export const mesInteiro = (p: Periodo): string | null => (soMesesInteiros(p) && p.de.slice(0, 7) === p.ate.slice(0, 7) ? p.de.slice(0, 7) : null);

/** Quanto do mês o período cobre (dias dentro ÷ dias do mês): a parte do que é do mês inteiro. */
export function fracaoDoMes(m: string, p: Periodo): number {
  const inicio = p.de > `${m}-01` ? p.de : `${m}-01`;
  const fim = p.ate < ultimoDia(m) ? p.ate : ultimoDia(m);
  return inicio > fim ? 0 : diasEntre(inicio, fim) / diasDoMes(m);
}

/** Intervalo [início, fim) do período, para colunas DATE (vencimento, pagamento, emissão). */
export const intervaloDoPeriodo = (p: Periodo) => ({ inicio: textoParaData(p.de), fim: textoParaData(somarDias(p.ate, 1)) });
const BRASILIA_MS = 3 * 60 * 60 * 1000;
/** O mesmo no relógio de Brasília, para colunas com hora (primeira entrega, retrabalho resolvido). */
export function intervaloDoPeriodoEmBrasilia(p: Periodo) {
  const { inicio, fim } = intervaloDoPeriodo(p);
  return { inicio: new Date(inicio.getTime() + BRASILIA_MS), fim: new Date(fim.getTime() + BRASILIA_MS) };
}

export const diasDoPeriodo = (p: Periodo) => diasEntre(p.de, p.ate);
export const dentroDoPeriodo = (dia: string, p: Periodo) => dia >= p.de && dia <= p.ate;

/** O período do mesmo tamanho logo antes: meses inteiros andam em meses, o resto em dias. */
export function periodoAnterior(p: Periodo): Periodo {
  if (soMesesInteiros(p)) {
    const n = mesesDoPeriodo(p).length;
    return { de: `${somarMeses(p.de.slice(0, 7), -n)}-01`, ate: ultimoDia(somarMeses(p.ate.slice(0, 7), -n)) };
  }
  const n = diasDoPeriodo(p);
  return { de: somarDias(p.de, -n), ate: somarDias(p.ate, -n) };
}

/** O próximo período do mesmo tamanho (a seta › do seletor). */
export function periodoSeguinte(p: Periodo): Periodo {
  if (soMesesInteiros(p)) {
    const n = mesesDoPeriodo(p).length;
    return { de: `${somarMeses(p.de.slice(0, 7), n)}-01`, ate: ultimoDia(somarMeses(p.ate.slice(0, 7), n)) };
  }
  const n = diasDoPeriodo(p);
  return { de: somarDias(p.de, n), ate: somarDias(p.ate, n) };
}

const MES_CURTO = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const curto = (m: string) => MES_CURTO[Number(m.slice(5, 7)) - 1]!;
const dm = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;

/** "novembro de 2026", "set–nov de 2026", "nov/26–fev/27" ou "05/11 a 20/11/2026". */
export function rotuloDoPeriodo(p: Periodo): string {
  const mes = mesInteiro(p);
  if (mes) return nomeDoMes(mes);
  const [m1, m2] = [p.de.slice(0, 7), p.ate.slice(0, 7)];
  if (soMesesInteiros(p)) {
    if (m1.slice(0, 4) === m2.slice(0, 4)) return m1.endsWith("-01") && m2.endsWith("-12") ? `ano de ${m1.slice(0, 4)}` : `${curto(m1)}–${curto(m2)} de ${m1.slice(0, 4)}`;
    return `${curto(m1)}/${m1.slice(2, 4)}–${curto(m2)}/${m2.slice(2, 4)}`;
  }
  return p.de.slice(0, 4) === p.ate.slice(0, 4) ? `${dm(p.de)} a ${dm(p.ate)}/${p.ate.slice(0, 4)}` : `${dm(p.de)}/${p.de.slice(0, 4)} a ${dm(p.ate)}/${p.ate.slice(0, 4)}`;
}

export type AtalhoDoPeriodo = { rotulo: string; periodo: Periodo };

/** Os atalhos do seletor, a partir de hoje. */
export function atalhosDoPeriodo(agora = new Date()): AtalhoDoPeriodo[] {
  const m = competenciaAtual(agora);
  const ano = m.slice(0, 4);
  const ultimos = (n: number): Periodo => ({ de: `${somarMeses(m, -(n - 1))}-01`, ate: ultimoDia(m) });
  return [
    { rotulo: "Este mês", periodo: periodoDoMes(m) },
    { rotulo: "Mês passado", periodo: periodoDoMes(somarMeses(m, -1)) },
    { rotulo: "Últimos 30 dias", periodo: { de: somarDias(hojeEmTexto(agora), -29), ate: hojeEmTexto(agora) } },
    { rotulo: "Últimos 3 meses", periodo: ultimos(3) },
    { rotulo: "Últimos 6 meses", periodo: ultimos(6) },
    { rotulo: "Últimos 12 meses", periodo: ultimos(12) },
    { rotulo: "Este ano", periodo: { de: `${ano}-01-01`, ate: `${ano}-12-31` } },
    { rotulo: "Ano passado", periodo: { de: `${Number(ano) - 1}-01-01`, ate: `${Number(ano) - 1}-12-31` } },
  ];
}
